'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { 
  signInWithEmailAndPassword, 
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  sendPasswordResetEmail
} from 'firebase/auth';
import EnvProbe from '@/components/EnvProbe';
import { auth, db } from '@/lib/firebase.client';
import { ensureUserDoc } from '@/lib/users';
import { doc, getDoc } from 'firebase/firestore';
import { getMasterPostAuthRedirect } from '@/lib/masterOnboarding';

export default function LoginPage() {
 const router = useRouter();
 const [form, setForm] = useState({ email: '', password: '' });
 const [loading, setLoading] = useState(false);
 const [err, setErr] = useState<string | null>(null);
 const [showPwd, setShowPwd] = useState(false);
 
 // Phone auth state
 const [phoneNumber, setPhoneNumber] = useState('');
 const [verificationCode, setVerificationCode] = useState('');
 const [isCodeSent, setIsCodeSent] = useState(false);
 const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
 const [phoneLoading, setPhoneLoading] = useState(false);
 const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
 
 // Forgot password state
 const [showForgotPassword, setShowForgotPassword] = useState(false);
 const [resetEmail, setResetEmail] = useState('');
 const [resetLoading, setResetLoading] = useState(false);
 const [resetSuccess, setResetSuccess] = useState(false);

 // Initialize Recaptcha
 useEffect(() => {
   if (typeof window !== 'undefined' && !recaptchaVerifierRef.current) {
     try {
       recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
         size: 'invisible',
         callback: () => {
           // reCAPTCHA solved, allow sendSMS
         },
         'expired-callback': () => {
           setErr('reCAPTCHA expired. Please try again.');
         }
       });
     } catch (error) {
       console.error('Recaptcha initialization error:', error);
     }
   }
   
   return () => {
     if (recaptchaVerifierRef.current) {
       recaptchaVerifierRef.current.clear();
       recaptchaVerifierRef.current = null;
     }
   };
 }, []);

 const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
 const { name, value } = e.target;
 setForm((f) => ({ ...f, [name]: value }));
 };

 async function resolvePostLoginRedirect(uid: string) {
 if (!db) return '/';
 const userSnap = await getDoc(doc(db, 'users', uid));
 const role = userSnap.data()?.role as string | undefined;
 return getMasterPostAuthRedirect(db, uid, role);
 }

 const onSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 setErr(null);
 setLoading(true);
 try {
 const { user } = await signInWithEmailAndPassword(auth, form.email.trim(), form.password);
 await ensureUserDoc(user);
 router.push(await resolvePostLoginRedirect(user.uid));
 } catch (e: any) {
 setErr(e?.message ?? 'Login failed');
 } finally {
 setLoading(false);
 }
 };

 const handleSendCode = async () => {
   if (!phoneNumber.trim()) {
     setErr('Please enter a phone number');
     return;
   }
   
   setErr(null);
   setPhoneLoading(true);
   try {
     if (!recaptchaVerifierRef.current) {
       throw new Error('reCAPTCHA not initialized');
     }
     
     const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
     const confirmation = await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifierRef.current);
     setConfirmationResult(confirmation);
     setIsCodeSent(true);
   } catch (e: any) {
     setErr(e?.message ?? 'Failed to send verification code');
   } finally {
     setPhoneLoading(false);
   }
 };

 const handleVerifyCode = async () => {
   if (!verificationCode.trim()) {
     setErr('Please enter the verification code');
     return;
   }
   
   if (!confirmationResult) {
     setErr('No confirmation result. Please send code again.');
     return;
   }
   
   setErr(null);
   setPhoneLoading(true);
   try {
     const { user } = await confirmationResult.confirm(verificationCode);
     await ensureUserDoc(user);
     router.push(await resolvePostLoginRedirect(user.uid));
   } catch (e: any) {
     setErr(e?.message ?? 'Invalid verification code');
   } finally {
     setPhoneLoading(false);
   }
 };

 const handleForgotPassword = async (e: React.FormEvent) => {
   e.preventDefault();
   if (!resetEmail.trim()) {
     setErr('Please enter your email address');
     return;
   }
   
   setErr(null);
   setResetLoading(true);
   setResetSuccess(false);
   try {
     await sendPasswordResetEmail(auth, resetEmail.trim());
     setResetSuccess(true);
   } catch (e: any) {
     setErr(e?.message ?? 'Failed to send password reset email');
   } finally {
     setResetLoading(false);
   }
 };

 return (
 <>
 <EnvProbe />
 <div className="min-h-[calc(100dvh-64px)] bg-gradient-to-b from-pink-500 via-pink-500 to-pink-600 flex items-center justify-center px-4 py-5 sm:py-8 md:py-10">
 <div className="w-full max-w-md">
 <div className="rounded-2xl bg-white/95 shadow-xl ring-1 ring-black/5 backdrop-blur p-4 sm:p-6 md:p-8 max-h-[90vh] sm:max-h-none overflow-y-auto sm:overflow-visible">
 <h1 className="text-2xl font-semibold text-center mb-1">Log in</h1>
 <p className="text-center text-gray-500 mb-4 sm:mb-6">Welcome back </p>

 {/* Email/Password Form */}
 {!showForgotPassword ? (
 <>
 <form onSubmit={onSubmit} className="space-y-3 sm:space-y-4">
 <div className="space-y-1.5">
 <label className="block text-sm font-medium text-gray-700">Email</label>
 <input
 name="email"
 type="email"
 autoComplete="email"
 required
 value={form.email}
 onChange={onChange}
 className="w-full rounded-lg border-gray-300 focus:border-pink-500 focus:ring-pink-500 p-2.5"
 placeholder="you@example.com"
 />
 </div>

 <div className="space-y-1.5">
 <label className="block text-sm font-medium text-gray-700">Password</label>
 <div className="relative">
 <input
 name="password"
 type={showPwd ? 'text' : 'password'}
 autoComplete="current-password"
 required
 value={form.password}
 onChange={onChange}
 className="w-full rounded-lg border-gray-300 focus:border-pink-500 focus:ring-pink-500 p-2.5 pr-10"
 placeholder="••••••••"
 />
 <button
 type="button"
 onClick={() => setShowPwd((v) => !v)}
 className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700"
 aria-label={showPwd ? 'Hide password' : 'Show password'}
 >
 {showPwd ? '🙈' : '👁️'}
 </button>
 </div>
 </div>

 {err && <div className="text-sm text-red-600">{err}</div>}

 <button
 type="submit"
 disabled={loading}
 className="w-full rounded-lg bg-pink-600 text-white py-2.5 font-medium hover:bg-pink-700 disabled:opacity-60 transition"
 >
 {loading ? 'Logging in…' : 'Log in'}
 </button>
 </form>

 <div className="mt-3 text-right">
 <button
 type="button"
 onClick={() => {
 setShowForgotPassword(true);
 setResetEmail(form.email);
 setErr(null);
 }}
 className="text-sm text-gray-500 hover:text-gray-700"
 >
 Forgot password?
 </button>
 </div>
 </>
 ) : (
 <>
 <div className="space-y-3 sm:space-y-4">
 <h2 className="text-lg font-semibold">Reset Password</h2>
 <form onSubmit={handleForgotPassword} className="space-y-3 sm:space-y-4">
 <div className="space-y-1.5">
 <label className="block text-sm font-medium text-gray-700">Email</label>
 <input
 type="email"
 autoComplete="email"
 required
 value={resetEmail}
 onChange={(e) => setResetEmail(e.target.value)}
 className="w-full rounded-lg border-gray-300 focus:border-pink-500 focus:ring-pink-500 p-2.5"
 placeholder="you@example.com"
 />
 </div>

 {err && <div className="text-sm text-red-600">{err}</div>}
 {resetSuccess && (
 <div className="text-sm text-green-600">
 Password reset link has been sent to your email if this address exists in our system.
 </div>
 )}

 <div className="flex gap-2">
 <button
 type="submit"
 disabled={resetLoading}
 className="flex-1 rounded-lg bg-pink-600 text-white py-2.5 font-medium hover:bg-pink-700 disabled:opacity-60 transition"
 >
 {resetLoading ? 'Sending…' : 'Send reset link'}
 </button>
 <button
 type="button"
 onClick={() => {
 setShowForgotPassword(false);
 setResetEmail('');
 setResetSuccess(false);
 setErr(null);
 }}
 className="px-4 rounded-lg border border-gray-300 text-gray-700 py-2.5 font-medium hover:bg-gray-50 transition"
 >
 Cancel
 </button>
 </div>
 </form>
 </div>
 </>
 )}

 {/* Phone Sign-in Section */}
 {!showForgotPassword && (
 <>
 <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
 <h3 className="text-sm font-medium text-gray-700 mb-3">Sign in with phone</h3>
 
 {!isCodeSent ? (
 <div className="space-y-3">
 <div className="space-y-1.5">
 <label className="block text-sm font-medium text-gray-700">Phone Number</label>
 <input
 type="tel"
 value={phoneNumber}
 onChange={(e) => setPhoneNumber(e.target.value)}
 placeholder="+1234567890"
 className="w-full rounded-lg border-gray-300 focus:border-pink-500 focus:ring-pink-500 p-2.5"
 />
 <p className="text-xs text-gray-500">Include country code (e.g., +1 for US)</p>
 </div>
 
 <button
 type="button"
 onClick={handleSendCode}
 disabled={phoneLoading || loading}
 className="w-full rounded-lg border border-gray-300 bg-white text-gray-700 py-2.5 font-medium hover:bg-gray-50 disabled:opacity-60 transition"
 >
 {phoneLoading ? 'Sending code…' : 'Send verification code'}
 </button>
 </div>
 ) : (
 <div className="space-y-3">
 <div className="space-y-1.5">
 <label className="block text-sm font-medium text-gray-700">Verification Code</label>
 <input
 type="text"
 value={verificationCode}
 onChange={(e) => setVerificationCode(e.target.value)}
 placeholder="Enter 6-digit code"
 maxLength={6}
 className="w-full rounded-lg border-gray-300 focus:border-pink-500 focus:ring-pink-500 p-2.5"
 />
 </div>
 
 <button
 type="button"
 onClick={handleVerifyCode}
 disabled={phoneLoading || loading}
 className="w-full rounded-lg bg-pink-600 text-white py-2.5 font-medium hover:bg-pink-700 disabled:opacity-60 transition"
 >
 {phoneLoading ? 'Verifying…' : 'Verify code'}
 </button>
 
 <button
 type="button"
 onClick={() => {
 setIsCodeSent(false);
 setVerificationCode('');
 setConfirmationResult(null);
 setErr(null);
 }}
 className="w-full text-sm text-gray-500 hover:text-gray-700"
 >
 Change phone number
 </button>
 </div>
 )}
 </div>
 </>
 )}

 <div className="mt-4 sm:mt-5 text-center text-sm text-gray-600">
 Don't have an account?{' '}
 <Link href="/auth/signup" className="font-medium text-pink-600 hover:text-pink-700">
 Sign up
 </Link>
 </div>
 </div>
 </div>
 </div>
 
 {/* reCAPTCHA container (hidden) */}
 <div id="recaptcha-container"></div>
 </>
 );
}